document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

function formatMoney(cents) {
  const currencyCode = document.documentElement.dataset.currency || 'USD';
  const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: currencyCode
  });

  return formatter.format((cents || 0) / 100);
}

function initMobileMenu() {
  const header = document.querySelector('[data-site-header]');
  if (!header || header.dataset.mobileMenuBound === 'true') return;

  const toggle = header.querySelector('[data-menu-toggle]');
  const panel = header.querySelector('[data-mobile-menu]');
  if (!toggle || !panel) return;

  header.dataset.mobileMenuBound = 'true';

  toggle.addEventListener('click', () => {
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isExpanded));
    panel.hidden = isExpanded;
    document.body.classList.toggle('menu-open', !isExpanded);
  });

  panel.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      document.body.classList.remove('menu-open');
    });
  });
}

function initMegaMenu() {
  const header = document.querySelector('[data-site-header]');
  if (!header || header.dataset.megaMenuBound === 'true') return;

  const megaMenu = header.querySelector('[data-mega-menu]');
  const triggers = [...header.querySelectorAll('[data-mega-trigger]')];
  const panels = [...header.querySelectorAll('[data-mega-panel]')];
  if (!megaMenu || !triggers.length || !panels.length) return;

  header.dataset.megaMenuBound = 'true';

  const isDesktop = () => window.matchMedia('(min-width: 861px)').matches;

  const closeAll = () => {
    megaMenu.hidden = true;
    header.classList.remove('is-mega-open');
    triggers.forEach((trigger) => {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    });
    panels.forEach((panel) => {
      panel.hidden = true;
    });
  };

  const openPanel = (panelName) => {
    if (!isDesktop()) return;

    megaMenu.hidden = false;
    header.classList.add('is-mega-open');
    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.megaTrigger === panelName;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-expanded', String(isActive));
    });
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.megaPanel !== panelName;
    });
  };

  triggers.forEach((trigger) => {
    const panelName = trigger.dataset.megaTrigger;

    const togglePanel = () => {
      const clickTime = Date.now();
      const previousClickTime = Number(header.dataset.megaLastInteractionAt || 0);
      if (clickTime - previousClickTime < 900) return false;

      header.dataset.megaLastInteractionAt = String(clickTime);
      if (trigger.getAttribute('aria-expanded') === 'true') {
        closeAll();
      } else {
        openPanel(panelName);
      }
      return true;
    };

    trigger.addEventListener('pointerdown', (event) => {
      // One physical press can be followed by duplicate click events in theme
      // editor and storefront contexts. Toggle once here, then ignore those.
      event.stopPropagation();
      if (togglePanel()) {
        header.dataset.megaIgnoreClickUntil = String(Date.now() + 1200);
      }
    });

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (Date.now() < Number(header.dataset.megaIgnoreClickUntil || 0)) return;
      togglePanel();
    });

  });

  document.addEventListener('pointerdown', (event) => {
    if (!header.contains(event.target)) closeAll();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || megaMenu.hidden) return;

    const activeTrigger = triggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');
    closeAll();
    activeTrigger?.focus();
  });

  window.addEventListener('resize', () => {
    if (!isDesktop()) closeAll();
  });
}

function initHeroVideo() {
  const video = document.querySelector('[data-hero-video]');
  const soundButton = document.querySelector('[data-hero-sound]');
  if (!video || !soundButton) return;

  soundButton.addEventListener('click', () => {
    video.muted = !video.muted;
    soundButton.setAttribute('aria-pressed', String(!video.muted));
    soundButton.textContent = video.muted ? 'Sound on' : 'Sound off';
  });
}

function initWishlist() {
  const toggles = [...document.querySelectorAll('[data-wishlist-toggle]')];
  if (!toggles.length) return;

  const storageKey = 'nativ-athlete-wishlist';
  let savedProducts = new Set();

  try {
    savedProducts = new Set(JSON.parse(window.localStorage.getItem(storageKey) || '[]'));
  } catch {
    // Saving remains a visual session affordance if storage is unavailable.
  }

  const updateToggle = (toggle) => {
    const isSaved = savedProducts.has(toggle.dataset.wishlistHandle);
    toggle.setAttribute('aria-pressed', String(isSaved));
    const label = toggle.querySelector('.visually-hidden');
    if (label) {
      label.textContent = isSaved
        ? `Remove ${toggle.dataset.wishlistHandle} from saved products`
        : `Save ${toggle.dataset.wishlistHandle}`;
    }
  };

  toggles.forEach((toggle) => {
    updateToggle(toggle);

    toggle.addEventListener('click', () => {
      const handle = toggle.dataset.wishlistHandle;
      if (!handle) return;

      if (savedProducts.has(handle)) {
        savedProducts.delete(handle);
      } else {
        savedProducts.add(handle);
      }

      try {
        window.localStorage.setItem(storageKey, JSON.stringify([...savedProducts]));
      } catch {
        // Keep the in-page state even if the browser blocks storage.
      }

      toggles
        .filter((item) => item.dataset.wishlistHandle === handle)
        .forEach(updateToggle);
    });
  });
}

function initProductShare() {
  document.querySelectorAll('[data-product-share]').forEach((button) => {
    if (button.dataset.productShareInitialized === 'true') return;
    button.dataset.productShareInitialized = 'true';

    const status = button.closest('[data-product-form]')?.querySelector('[data-product-share-status]');

    button.addEventListener('click', async () => {
      const shareData = {
        title: button.dataset.productShareTitle || document.title,
        url: window.location.href
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
          if (status) status.textContent = 'Product link shared.';
          return;
        }

        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(window.location.href);
          if (status) status.textContent = 'Product link copied.';
          return;
        }

        if (status) status.textContent = 'Copy this product link from your browser address bar.';
      } catch (error) {
        if (error?.name !== 'AbortError' && status) {
          status.textContent = 'Unable to share this product right now.';
        }
      }
    });
  });
}

function initProductQuantityControls(section) {
  section.querySelectorAll('[data-product-quantity]').forEach((control) => {
    if (control.dataset.productQuantityInitialized === 'true') return;
    control.dataset.productQuantityInitialized = 'true';

    const input = control.querySelector('[data-product-quantity-input], .product-form__quantity-input');
    if (!input) return;

    const sanitizeQuantity = () => {
      const nextValue = Math.max(1, Number.parseInt(input.value, 10) || 1);
      input.value = String(nextValue);
    };

    control.querySelectorAll('[data-product-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        const currentValue = Math.max(1, Number.parseInt(input.value, 10) || 1);
        const nextValue = button.dataset.productQuantityChange === 'increase'
          ? currentValue + 1
          : Math.max(1, currentValue - 1);

        input.value = String(nextValue);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    input.addEventListener('change', sanitizeQuantity);
    input.addEventListener('blur', sanitizeQuantity);
  });
}

function initFeaturedAudienceCollection(section) {
  const tabs = [...section.querySelectorAll('[data-featured-audience-tab]')];
  const panels = [...section.querySelectorAll('[data-featured-audience-panel]')];
  const collectionLink = section.querySelector('[data-featured-audience-link]');
  const rails = [...section.querySelectorAll('[data-featured-rail]')];
  const railButtons = [...section.querySelectorAll('[data-featured-rail-control]')];

  if (!tabs.length || !panels.length) return;

  const getActiveRail = () =>
    section.querySelector('[data-featured-audience-panel].is-active [data-featured-rail]');

  const updateRailControls = () => {
    const rail = getActiveRail();
    const maxScroll = rail ? Math.max(rail.scrollWidth - rail.clientWidth, 0) : 0;

    railButtons.forEach((button) => {
      const isNext = button.dataset.featuredRailControl === 'next';
      const isAtEdge = !rail || maxScroll < 2 || (isNext ? rail.scrollLeft >= maxScroll - 1 : rail.scrollLeft <= 1);
      button.disabled = isAtEdge;
    });
  };

  const scrollActiveRail = (direction) => {
    const rail = getActiveRail();
    if (!rail) return;

    const cardWidth = rail.querySelector('.product-card')?.getBoundingClientRect().width || 0;
    const distance = Math.max(cardWidth * 2, rail.clientWidth * 0.72);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    rail.scrollBy({
      left: direction * distance,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  };

  const activateAudience = (audience) => {
    const activeTab = tabs.find((tab) => tab.dataset.featuredAudienceTab === audience);
    if (!activeTab) return;

    tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.featuredAudiencePanel === audience;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    if (collectionLink && activeTab.dataset.audienceUrl) {
      collectionLink.href = activeTab.dataset.audienceUrl;
    }

    window.requestAnimationFrame(updateRailControls);
  };

  railButtons.forEach((button) => {
    button.addEventListener('click', () => {
      scrollActiveRail(button.dataset.featuredRailControl === 'next' ? 1 : -1);
    });
  });

  rails.forEach((rail) => {
    rail.addEventListener('scroll', updateRailControls, { passive: true });
  });

  window.addEventListener('resize', updateRailControls);

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => {
      activateAudience(tab.dataset.featuredAudienceTab);
    });

    tab.addEventListener('keydown', (event) => {
      let nextIndex = null;

      if (event.key === 'ArrowRight') {
        nextIndex = (index + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      }

      if (nextIndex === null) return;

      event.preventDefault();
      const nextTab = tabs[nextIndex];
      nextTab.focus();
      activateAudience(nextTab.dataset.featuredAudienceTab);
    });
  });

  updateRailControls();
}

function initGallery(section) {
  const items = [...section.querySelectorAll('[data-media-item]')];
  const buttons = [...section.querySelectorAll('[data-media-target]')];
  const viewer = section.querySelector('[data-gallery-viewer]');

  const emptyGallery = {
    activateItem: () => {},
    findColorMediaId: () => null
  };

  if (!items.length || !buttons.length) return emptyGallery;

  const colorAliases = {
    black: ['black'],
    white: ['white', 'ivory', 'cream'],
    pink: ['pink', 'blush', 'rose', 'apricot'],
    yellow: ['yellow', 'lemon', 'mustard'],
    purple: ['purple', 'pale lilac', 'lavender', 'violet'],
    coffee: ['coffee', 'mocha', 'cocoa', 'brown', 'espresso'],
    gray: ['gray', 'grey', 'charcoal', 'dark gray', 'dark grey'],
    grey: ['gray', 'grey', 'charcoal', 'dark gray', 'dark grey'],
    green: ['green', 'olive', 'bean green'],
    blue: ['blue', 'navy', 'lake blue', 'gray blue', 'grey blue']
  };

  const normalizeMediaText = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const activateItem = (id, shouldScrollThumbnail = false) => {
    items.forEach((item) => {
      const isActive = item.dataset.mediaItem === String(id);
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', String(!isActive));
    });

    buttons.forEach((button) => {
      const isActive = button.dataset.mediaTarget === String(id);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', String(isActive));

      if (isActive && shouldScrollThumbnail) {
        button.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    });

    const activeButton = buttons.find((button) => button.dataset.mediaTarget === String(id));
    if (viewer && activeButton?.dataset.mediaAspectRatio) {
      viewer.style.setProperty('--active-media-aspect-ratio', activeButton.dataset.mediaAspectRatio);
    }
  };

  buttons.forEach((button) => {
    button.addEventListener('click', () => activateItem(button.dataset.mediaTarget, true));
  });

  const findColorMediaId = (color) => {
    const colorKey = normalizeMediaText(color);
    if (!colorKey) return null;

    const colorTerms = [colorKey, ...(colorAliases[colorKey] || [])]
      .map(normalizeMediaText)
      .filter(Boolean);

    const matchingButtons = buttons.filter((button) => {
      const mediaAlt = ` ${normalizeMediaText(button.dataset.mediaAlt)} `;
      return colorTerms.some((term) => mediaAlt.includes(` ${term} `));
    });

    const audience = new URLSearchParams(window.location.search).get('audience');
    const model = audience === 'women' ? 'female' : audience === 'men' ? 'male' : '';
    const preferred = model ? matchingButtons.find((button) => {
      const words = normalizeMediaText(button.dataset.mediaAlt).split(' ');
      return words.includes(model) && words.includes('front');
    }) : null;
    return (preferred || matchingButtons[0])?.dataset.mediaTarget || null;
  };

  return { activateItem, findColorMediaId };
}

function initStickyBar(section) {
  const stickyBar = section.querySelector('[data-sticky-atc]');
  const form = section.querySelector('[data-product-form]');

  if (!stickyBar || !form) return;

  if (!('IntersectionObserver' in window)) {
    stickyBar.hidden = false;
    return;
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      // Keep the first product view clear; show the shortcut only after its form has scrolled above the viewport.
      const hasScrolledPastForm = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      stickyBar.hidden = !hasScrolledPastForm;
    },
    {
      threshold: 0.15
    }
  );

  observer.observe(form);
}

function initProductSet(section) {
  const set = section.querySelector('[data-product-set]');
  if (!set) return () => {};

  const button = set.querySelector('[data-add-product-set]');
  const price = set.querySelector('[data-product-set-price]');
  const message = set.querySelector('[data-product-set-message]');
  const selectedVariantInput = section.querySelector('[data-variant-id-input]');
  const pairedVariantId = set.dataset.pairedVariantId;
  const pairedVariantPrice = Number(set.dataset.pairedVariantPrice || 0);
  const pairedAvailable = set.dataset.pairedAvailable === 'true';

  if (!button || !pairedVariantId || !selectedVariantInput) return () => {};

  const updateSet = (variant) => {
    const isAvailable = Boolean(variant?.available && pairedAvailable);

    if (price && variant) {
      price.textContent = formatMoney((variant.price || 0) + pairedVariantPrice);
    }

    button.disabled = !isAvailable;
    button.textContent = isAvailable ? button.dataset.defaultLabel : 'Set unavailable';

    if (!isAvailable && message) {
      message.textContent = 'Choose an available option to add the set.';
    } else if (message) {
      message.textContent = '';
    }
  };

  button.addEventListener('click', async () => {
    const currentVariantId = selectedVariantInput.value;
    if (!currentVariantId || button.disabled) return;

    button.disabled = true;
    button.textContent = 'Adding set...';
    if (message) message.textContent = '';

    try {
      const response = await fetch(set.dataset.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            { id: Number(currentVariantId), quantity: 1 },
            { id: Number(pairedVariantId), quantity: 1 }
          ]
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'The set could not be added to your cart.');
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { trigger: button }
      }));
      button.disabled = false;
      button.textContent = button.dataset.defaultLabel;
    } catch (error) {
      button.disabled = false;
      button.textContent = button.dataset.defaultLabel;
      if (message) {
        message.textContent = error instanceof Error ? error.message : 'The set could not be added to your cart.';
      }
    }
  });

  return updateSet;
}

function initProductSection(section) {
  const gallery = initGallery(section);
  initStickyBar(section);
  initProductQuantityControls(section);

  const variantDataElement = section.querySelector('[data-variant-json]');
  if (!variantDataElement) return;

  let variants = [];
  try {
    variants = JSON.parse(variantDataElement.textContent || '[]');
  } catch (error) {
    console.error('Unable to parse variant JSON', error);
    return;
  }

  if (!variants.length) return;

  const optionGroups = [...section.querySelectorAll('[data-product-option]')];
  const colorOptionIndex = optionGroups.findIndex((group) =>
    ['color', 'colour'].includes(group.dataset.productOptionName)
  );
  const variantInputs = [
    ...section.querySelectorAll('[data-variant-id-input]'),
    ...section.querySelectorAll('[data-sticky-variant-input]')
  ];
  const priceElement = section.querySelector('[data-product-price]');
  const stickyPriceElement = section.querySelector('[data-sticky-price]');
  const messageElement = section.querySelector('[data-product-message]');
  const buttons = [...section.querySelectorAll('[data-add-to-cart], [data-sticky-submit]')];
  const productUrl = section.dataset.productUrl;
  const updateProductSet = initProductSet(section);

  const getSelections = () =>
    optionGroups.map((group) => group.querySelector('input:checked')?.value || null);

  const findVariant = (selections) =>
    variants.find((variant) =>
      selections.every((value, index) => value === variant[`option${index + 1}`])
    ) || null;

  const getColorMediaId = (variant) => {
    if (!variant || colorOptionIndex === -1) return null;

    return gallery.findColorMediaId(variant[`option${colorOptionIndex + 1}`]);
  };

  const updatePillStates = () => {
    optionGroups.forEach((group) => {
      group.querySelectorAll('.option-pill').forEach((pill) => {
        const input = pill.querySelector('input');
        pill.classList.toggle('is-selected', input.checked);
      });
    });
  };

  const updateAvailability = (selections) => {
    optionGroups.forEach((group, groupIndex) => {
      group.querySelectorAll('input').forEach((input) => {
        const testSelections = [...selections];
        testSelections[groupIndex] = input.value;

        const hasAvailableMatch = variants.some((variant) =>
          testSelections.every((value, optionIndex) => {
            if (!value) return true;
            return value === variant[`option${optionIndex + 1}`];
          }) && variant.available
        );

        input.disabled = !hasAvailableMatch;
        input.closest('.option-pill')?.classList.toggle('is-disabled', !hasAvailableMatch);
      });
    });
  };

  const updateButtons = (variant) => {
    buttons.forEach((button) => {
      const defaultLabel = button.dataset.defaultLabel || 'Add to cart';
      const isAvailable = Boolean(variant && variant.available);
      button.disabled = !isAvailable;
      button.textContent = isAvailable ? defaultLabel : 'Sold out';
    });
  };

  const updatePrice = (variant) => {
    if (!variant || !priceElement) return;

    const compareMarkup =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<span class="price__current">${formatMoney(variant.price)}</span><span class="price__compare">${formatMoney(variant.compare_at_price)}</span>`
        : `<span class="price__current">${formatMoney(variant.price)}</span>`;

    priceElement.innerHTML = compareMarkup;

    if (stickyPriceElement) {
      stickyPriceElement.textContent = formatMoney(variant.price);
    }
  };

  const syncVariant = (variant) => {
    variantInputs.forEach((input) => {
      input.value = variant ? variant.id : '';
    });

    updateButtons(variant);
    updateProductSet(variant);

    if (variant) {
      updatePrice(variant);
      if (messageElement) {
        messageElement.textContent = variant.available ? '' : 'This option is sold out.';
      }

      const matchingMediaId =
        getColorMediaId(variant) ||
        variant.featured_media?.id ||
        variant.featured_image?.id;

      if (matchingMediaId) {
        gallery.activateItem(matchingMediaId, true);
      }

      if (productUrl && window.history.replaceState) {
        const destination = new URL(productUrl, window.location.origin);
        destination.searchParams.set('variant', variant.id);
        const audience = new URLSearchParams(window.location.search).get('audience');
        if (audience === 'women' || audience === 'men') destination.searchParams.set('audience', audience);
        window.history.replaceState({}, '', destination.pathname + destination.search);
      }
    } else {
      if (messageElement) {
        messageElement.textContent = 'That option combination is unavailable.';
      }
    }
  };

  const handleOptionChange = () => {
    const selections = getSelections();
    updateAvailability(selections);
    updatePillStates();
    syncVariant(findVariant(selections));
  };

  optionGroups.forEach((group) => {
    group.addEventListener('change', handleOptionChange);
  });

  handleOptionChange();
}

function initCartDrawer() {
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;

  let previousFocus = null;
  const cartRoot = window.Shopify?.routes?.root || '/';
  const cartUrl = (path) => `${cartRoot.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

  const panel = () => drawer.querySelector('.cart-overlay__panel');
  const getFocusableElements = () =>
    [...(panel()?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])') || [])]
      .filter((element) => !element.hidden);

  const updateCartCount = (cart) => {
    if (!cart || typeof cart.item_count !== 'number') return;

    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = String(cart.item_count);
    });

    document.querySelectorAll('[data-cart-count-link]').forEach((link) => {
      link.setAttribute('aria-label', `Cart, ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}`);
    });
  };

  const getCart = async () => {
    const response = await fetch(cartUrl('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Unable to refresh the cart.');
    return response.json();
  };

  const refreshDrawer = async () => {
    const sectionUrl = new URL(window.location.href);
    sectionUrl.searchParams.set('sections', 'cart-drawer');

    const response = await fetch(sectionUrl.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) throw new Error('Unable to refresh the cart drawer.');

    const sections = await response.json();
    const template = document.createElement('template');
    template.innerHTML = (sections['cart-drawer'] || '').trim();
    const nextDrawer = template.content.querySelector('[data-cart-drawer]');
    if (!nextDrawer) throw new Error('Cart drawer markup was unavailable.');

    drawer.innerHTML = nextDrawer.innerHTML;
  };

  const openDrawer = (trigger) => {
    previousFocus = trigger || document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-overlay-open');

    window.requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      panel()?.querySelector('[data-cart-drawer-close]')?.focus();
    });
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-overlay-open');

    window.setTimeout(() => {
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 260);

    if (previousFocus instanceof HTMLElement) previousFocus.focus();
  };

  const changeLine = async (line, quantity) => {
    drawer.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(cartUrl('cart/change.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ line, quantity })
      });

      if (!response.ok) throw new Error('Unable to update the cart.');

      const cart = await response.json();
      updateCartCount(cart);
      await refreshDrawer();
    } catch (error) {
      console.error(error);
      window.location.assign(cartUrl('cart'));
    } finally {
      drawer.removeAttribute('aria-busy');
    }
  };

  document.querySelectorAll('[data-cart-drawer-open]').forEach((opener) => {
    opener.addEventListener('click', async (event) => {
      event.preventDefault();
      openDrawer(opener);

      try {
        const cart = await getCart();
        updateCartCount(cart);
        await refreshDrawer();
      } catch (error) {
        console.error(error);
      }
    });
  });

  drawer.addEventListener('click', (event) => {
    const closeTrigger = event.target.closest('[data-cart-drawer-close]');
    if (closeTrigger) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const removeButton = event.target.closest('[data-cart-drawer-remove]');
    if (removeButton) {
      event.preventDefault();
      const item = removeButton.closest('[data-cart-drawer-item]');
      const line = Number.parseInt(item?.dataset.cartLine || '', 10);
      if (line) changeLine(line, 0);
      return;
    }

    const quantityButton = event.target.closest('[data-cart-drawer-quantity-button]');
    if (!quantityButton) return;

    const item = quantityButton.closest('[data-cart-drawer-item]');
    const input = item?.querySelector('[data-cart-drawer-quantity-input]');
    const line = Number.parseInt(item?.dataset.cartLine || '', 10);
    if (!input || !line) return;

    const currentValue = Number.parseInt(input.value, 10) || 0;
    const nextValue = quantityButton.dataset.cartDrawerQuantityButton === 'increase'
      ? currentValue + 1
      : Math.max(0, currentValue - 1);

    input.value = String(nextValue);
    changeLine(line, nextValue);
  });

  drawer.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-drawer-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-drawer-item]');
    const line = Number.parseInt(item?.dataset.cartLine || '', 10);
    if (!line) return;

    const nextValue = Math.max(0, Number.parseInt(input.value, 10) || 0);
    input.value = String(nextValue);
    changeLine(line, nextValue);
  });

  document.querySelectorAll('[data-product-form], .sticky-atc__form').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      const submitter = event.submitter;
      if (!submitter?.matches('[data-add-to-cart], [data-sticky-submit]')) return;

      event.preventDefault();
      const defaultLabel = submitter.dataset.defaultLabel || submitter.textContent;
      submitter.disabled = true;
      submitter.textContent = 'Adding...';

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Unable to add this product to the cart.');
        }

        const cart = await getCart();
        updateCartCount(cart);
        openDrawer(submitter);
        await refreshDrawer();
      } catch (error) {
        const message = form.querySelector('[data-product-message]');
        if (message) message.textContent = error instanceof Error ? error.message : 'Unable to add this product to the cart.';
      } finally {
        submitter.disabled = false;
        submitter.textContent = defaultLabel;
      }
    });
  });

  document.addEventListener('cart:updated', async (event) => {
    openDrawer(event.detail?.trigger);

    try {
      const cart = await getCart();
      updateCartCount(cart);
      await refreshDrawer();
    } catch (error) {
      console.error(error);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (drawer.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}

function initCartPage() {
  const form = document.querySelector('[data-cart-form]');
  if (!form) return;

  const updateButton = form.querySelector('[data-cart-update]');
  const submitUpdate = () => {
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(updateButton || undefined);
    } else {
      form.submit();
    }
  };

  form.querySelectorAll('[data-cart-quantity]').forEach((control) => {
    const input = control.querySelector('[data-cart-quantity-input]');
    if (!input) return;

    control.querySelectorAll('[data-cart-quantity-button]').forEach((button) => {
      button.addEventListener('click', () => {
        const currentValue = Number.parseInt(input.value, 10) || 0;
        const nextValue = button.dataset.cartQuantityButton === 'increase'
          ? currentValue + 1
          : Math.max(0, currentValue - 1);

        input.value = String(nextValue);
        submitUpdate();
      });
    });

    input.addEventListener('change', () => {
      const nextValue = Math.max(0, Number.parseInt(input.value, 10) || 0);
      input.value = String(nextValue);
      submitUpdate();
    });
  });
}

function initTheme() {
  initMobileMenu();
  initMegaMenu();
  initHeroVideo();
  initWishlist();
  initProductShare();
  initCartDrawer();
  document.querySelectorAll('[data-featured-audience-collection]').forEach((section) => {
    initFeaturedAudienceCollection(section);
  });
  document.querySelectorAll('[data-product-section]').forEach((section) => {
    initProductSection(section);
  });
  initCartPage();
}

function initHeaderControls() {
  initMobileMenu();
  initMegaMenu();
}

function bootstrapTheme() {
  initTheme();

  // Theme sections can finish mounting after the deferred asset executes.
  // A second, guarded pass ensures header controls bind to the final markup.
  window.requestAnimationFrame(initHeaderControls);
}

// Shopify can load this asset after the document is already interactive in preview/live mode.
// Initialize immediately in that case so header controls are always wired up.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrapTheme, { once: true });
} else {
  bootstrapTheme();
}
